import { useState } from 'react';
import { Card, Badge, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './Settings.module.css';

interface TenantUser {
  id: string;
  name: string;
  email: string | null;
  role: string;
  is_active?: boolean;
  created_at: string;
}

interface TenantConfig {
  user_count: number;
  max_users: number;
}

const roleLabel: Record<string, string> = {
  tenant_owner: 'オーナー',
  tenant_manager: 'マネージャー',
  tenant_staff: 'スタッフ',
};
const roleBadge: Record<string, 'purple' | 'blue' | 'gray'> = {
  tenant_owner: 'purple',
  tenant_manager: 'blue',
  tenant_staff: 'gray',
};

export function Staff() {
  const { user: authUser } = useAuthStore();
  const canManageStaff = authUser?.role === 'tenant_owner' || authUser?.role === 'tenant_manager';
  const isOwner = authUser?.role === 'tenant_owner';
  // マネージャーはオーナーを操作できない
  const canManageTarget = (u: TenantUser) => !(authUser?.role === 'tenant_manager' && u.role === 'tenant_owner');

  const { data: users, loading, refetch } = useApi<TenantUser[]>('/tenant/users');
  const { data: config } = useApi<TenantConfig>('/tenant/config');
  const maxUsers = config?.max_users ?? 0;
  const activeCount = (users ?? []).filter(u => u.is_active !== false).length;

  // 招待
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'tenant_staff' });
  const [inviteResult, setInviteResult] = useState<{ temp_password: string } | null>(null);

  // 編集
  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'tenant_staff' });

  // 無効化確認
  const [disableTarget, setDisableTarget] = useState<TenantUser | null>(null);

  // パスワードリセット
  const [resetTarget, setResetTarget] = useState<TenantUser | null>(null);
  const [resetResult, setResetResult] = useState<{ temp_password: string } | null>(null);

  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteForm({ name: '', email: '', role: 'tenant_staff' });
    setInviteResult(null);
  };

  const handleInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast('名前とメールアドレスは必須です', 'error');
      return;
    }
    try {
      const result = await api<{ temp_password: string }>('/tenant/users/invite', {
        method: 'POST',
        body: JSON.stringify(inviteForm),
      });
      setInviteResult(result);
      toast('スタッフを招待しました', 'success');
      refetch();
    } catch (e: any) {
      toast(e?.message || '招待に失敗しました', 'error');
    }
  };

  const openEdit = (u: TenantUser) => {
    setEditForm({ name: u.name, email: u.email ?? '', role: u.role });
    setEditUser(u);
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast('名前とメールアドレスは必須です', 'error');
      return;
    }
    try {
      await api(`/tenant/users/${editUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      toast('スタッフを更新しました', 'success');
      setEditUser(null);
      refetch();
    } catch (e: any) {
      toast(e?.message || '更新に失敗しました', 'error');
    }
  };

  const handleDisable = async () => {
    if (!disableTarget) return;
    try {
      await api(`/tenant/users/${disableTarget.id}`, { method: 'DELETE' });
      toast('スタッフを無効化しました', 'success');
      setDisableTarget(null);
      refetch();
    } catch (e: any) {
      toast(e?.message || '無効化に失敗しました', 'error');
    }
  };

  const handleActivate = async (u: TenantUser) => {
    try {
      await api(`/tenant/users/${u.id}/activate`, { method: 'POST' });
      toast('スタッフを再有効化しました', 'success');
      refetch();
    } catch (e: any) {
      toast(e?.message || '再有効化に失敗しました', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    try {
      const result = await api<{ temp_password: string }>(`/tenant/users/${resetTarget.id}/reset-password`, { method: 'POST' });
      setResetResult(result);
      toast('パスワードを再発行しました', 'success');
    } catch (e: any) {
      toast(e?.message || 'パスワード再発行に失敗しました', 'error');
    }
  };

  const closeResetModal = () => { setResetTarget(null); setResetResult(null); };

  if (!canManageStaff) {
    return (
      <div className={styles.page}>
        <Card title="👤 スタッフ管理">
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)', fontSize: 13 }}>
            このページはオーナーまたはマネージャーのみアクセス可能です。
          </div>
        </Card>
      </div>
    );
  }

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      <Card title="👤 スタッフ管理" actions={
        canManageStaff ? (
          <button className={styles.staffAddBtn} onClick={() => setShowInvite(true)}>＋ スタッフ追加</button>
        ) : undefined
      }>
        <div className={styles.staffHeader}>
          <span className={styles.staffCount}>{activeCount} / {maxUsers}名（有効）</span>
        </div>
        <div className={styles.staffNote}>
          {maxUsers > activeCount ? `残り${maxUsers - activeCount}名追加可能` : 'プラン上限に達しています'}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>名前</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>メールアドレス</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>権限</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>ステータス</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>作成日</th>
              {canManageStaff && <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map(u => {
              const isSelf = u.id === authUser?.id;
              const isActive = u.is_active !== false;
              const canManage = canManageTarget(u);
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>
                    {u.name}{isSelf && <span style={{ fontSize: 10, color: 'var(--ink3)', marginLeft: 6 }}>(自分)</span>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink2)' }}>{u.email ?? '-'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge variant={roleBadge[u.role] ?? 'gray'}>{roleLabel[u.role] ?? u.role}</Badge>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge variant={isActive ? 'green' : 'gray'}>{isActive ? '有効' : '無効'}</Badge>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ink3)' }}>
                    {new Date(u.created_at).toLocaleDateString('ja-JP')}
                  </td>
                  {canManageStaff && (
                    <td style={{ padding: '10px 12px' }}>
                      {canManage ? (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className={styles.planBtn} onClick={() => openEdit(u)}>編集</button>
                          {isActive && (
                            <button className={styles.planBtn} onClick={() => setResetTarget(u)}>パスワード再発行</button>
                          )}
                          {isActive && !isSelf && (
                            <button className={styles.planBtn} style={{ color: '#dc2626', borderColor: '#fecaca' }}
                              onClick={() => setDisableTarget(u)}>無効化</button>
                          )}
                          {!isActive && (
                            <button className={styles.planBtn} style={{ color: '#059669', borderColor: '#a7f3d0' }}
                              onClick={() => handleActivate(u)}>再有効化</button>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 6, fontSize: 11, color: 'var(--ink3)', lineHeight: 1.8 }}>
          <div><strong>オーナー:</strong> 全権限（スタッフ管理・テナント設定・請求情報）</div>
          <div><strong>マネージャー:</strong> 全データ閲覧・編集、スタッフ招待・編集</div>
          <div><strong>スタッフ:</strong> 自分の担当顧客・取引のみ閲覧・編集</div>
        </div>
      </Card>

      {/* 招待モーダル */}
      <Modal open={showInvite} onClose={closeInviteModal} title="スタッフ招待" footer={
        inviteResult ? (
          <button className={styles.saveBtn} onClick={closeInviteModal}>閉じる</button>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.planBtn} onClick={closeInviteModal}>キャンセル</button>
            <button className={styles.saveBtn} onClick={handleInvite}>招待する</button>
          </div>
        )
      }>
        {inviteResult ? (
          <div className={styles.inviteResult}>
            <div className={styles.inviteSuccess}>✅ 招待が完了しました</div>
            <div className={styles.inviteInfo}>
              <div className={styles.inviteLabel}>仮パスワード</div>
              <div className={styles.invitePassword}>{inviteResult.temp_password}</div>
            </div>
            <div className={styles.inviteNote}>
              このパスワードをスタッフにお伝えください。初回ログイン時にパスワード変更をお願いしてください。
            </div>
          </div>
        ) : (
          <div className={styles.inviteForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>名前 *</label>
              <input className={styles.formInput} value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="山田 花子" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>メールアドレス *</label>
              <input className={styles.formInput} type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="yamada@example.com" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>権限</label>
              <select className={styles.formInput} value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })}>
                <option value="tenant_staff">スタッフ</option>
                <option value="tenant_manager">マネージャー</option>
                {isOwner && <option value="tenant_owner">オーナー</option>}
              </select>
            </div>
          </div>
        )}
      </Modal>

      {/* 編集モーダル */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="スタッフ編集" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.planBtn} onClick={() => setEditUser(null)}>キャンセル</button>
          <button className={styles.saveBtn} onClick={handleSaveEdit}>更新</button>
        </div>
      }>
        <div className={styles.inviteForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>名前 *</label>
            <input className={styles.formInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>メールアドレス *</label>
            <input className={styles.formInput} type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>権限</label>
            <select className={styles.formInput} value={editForm.role}
              onChange={e => setEditForm({ ...editForm, role: e.target.value })}
              disabled={editUser?.id === authUser?.id}>
              <option value="tenant_staff">スタッフ</option>
              <option value="tenant_manager">マネージャー</option>
              {(isOwner || editForm.role === 'tenant_owner') && <option value="tenant_owner">オーナー</option>}
            </select>
            {editUser?.id === authUser?.id && (
              <div style={{ fontSize: 10, color: 'var(--ink3)', marginTop: 4 }}>自分自身のロールは変更できません</div>
            )}
          </div>
        </div>
      </Modal>

      {/* 無効化確認 */}
      <Modal open={!!disableTarget} onClose={() => setDisableTarget(null)} title="スタッフ無効化" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.planBtn} onClick={() => setDisableTarget(null)}>キャンセル</button>
          <button className={styles.saveBtn} style={{ background: '#dc2626' }} onClick={handleDisable}>無効化する</button>
        </div>
      }>
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          <strong>{disableTarget?.name}</strong>（{disableTarget?.email ?? '-'}）を無効化しますか？<br />
          無効化後はログインできなくなりますが、担当履歴はそのまま残ります。再有効化は一覧から可能です。
        </p>
      </Modal>

      {/* パスワード再発行 */}
      <Modal open={!!resetTarget} onClose={closeResetModal} title="パスワード再発行" footer={
        resetResult ? (
          <button className={styles.saveBtn} onClick={closeResetModal}>閉じる</button>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.planBtn} onClick={closeResetModal}>キャンセル</button>
            <button className={styles.saveBtn} onClick={handleResetPassword}>再発行する</button>
          </div>
        )
      }>
        {resetResult ? (
          <div className={styles.inviteResult}>
            <div className={styles.inviteSuccess}>✅ パスワードを再発行しました</div>
            <div className={styles.inviteInfo}>
              <div className={styles.inviteLabel}>新しい仮パスワード</div>
              <div className={styles.invitePassword}>{resetResult.temp_password}</div>
            </div>
            <div className={styles.inviteNote}>
              このパスワードを対象者にお伝えください。初回ログイン時にパスワード変更をお願いしてください。
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 13, lineHeight: 1.6 }}>
            <strong>{resetTarget?.name}</strong>（{resetTarget?.email ?? '-'}）のパスワードを再発行しますか？<br />
            既存のパスワードは無効になり、新しい仮パスワードが表示されます。
          </p>
        )}
      </Modal>
    </div>
  );
}
